import React from 'react'
import { supabase } from '@/lib/supabase'
import { applyOptimisticReactionToggle } from '@/shared/utils/reactions'
import {
  fetchCommunityCardReactions,
  toggleCardReaction,
  type CommunityReaction,
} from '../services/communityService'

type UseCommunityCardReactionsResult = {
  loading: boolean
  reactions: CommunityReaction[]
  toggleEmoji: (emoji: string) => Promise<void>
  reload: () => Promise<void>
}

export function useCommunityCardReactions(
  opportunityId: number | null,
  active: boolean,
): UseCommunityCardReactionsResult {
  const [loading, setLoading] = React.useState(false)
  const [reactions, setReactions] = React.useState<CommunityReaction[]>([])

  const load = React.useCallback(async () => {
    if (!opportunityId || !active) {
      setReactions([])
      return
    }

    setLoading(true)
    try {
      const data = await fetchCommunityCardReactions(opportunityId)
      setReactions(data)
    } catch {
      setReactions([])
    } finally {
      setLoading(false)
    }
  }, [active, opportunityId])

  React.useEffect(() => {
    if (!opportunityId || !active) {
      setReactions([])
      return
    }

    let alive = true
    let interval: any = null

    ;(async () => {
      await load()
      if (!alive) return
    })()

    const channel = supabase
      .channel(`community_card:${opportunityId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'opportunity_community_reactions',
          filter: `opportunity_id=eq.${opportunityId}`,
        } as any,
        () => {
          load().catch(() => {
            // Erros de reload sÇ½o silenciosamente ignorados; o prÇ®ximo evento atualiza o estado.
          })
        },
      )
      .subscribe()

    // Fallback: pequeno polling para garantir atualizaçõees
    // mesmo se o canal realtime tiver algum problema.
    interval = setInterval(() => {
      load().catch(() => {
        // silencioso; próximo ciclo corrige
      })
    }, 3500)

    return () => {
      try {
        supabase.removeChannel(channel)
      } catch {
        // noop
      }
      try {
        if (interval) clearInterval(interval)
      } catch {
        // noop
      }
      alive = false
    }
  }, [active, load, opportunityId])

  const toggleEmoji = React.useCallback(
    async (emoji: string) => {
      if (!opportunityId || !active) return

      const trimmed = String(emoji || '').trim()
      if (!trimmed) return

      setReactions((prev) => applyOptimisticReactionToggle(prev, trimmed))

      void toggleCardReaction(opportunityId, trimmed).catch((error) => {
        // eslint-disable-next-line no-console
        console.error('[Community] toggleCardReaction error', error)
        load().catch(() => {
          // silencioso; próximo ciclo/realtime corrige
        })
      })
    },
    [active, load, opportunityId],
  )

  return {
    loading,
    reactions,
    toggleEmoji,
    reload: load,
  }
}
